import React, { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { LeftOutlined, SearchOutlined } from '@ant-design/icons'
import { Spin } from 'antd'
import { useTranslation } from 'react-i18next'

import { getGradientById } from '../../utils/color'
import { useAppDispatch } from '../../utils/redux/store'
import {
  attemptGetPlaylistsFolderList,
  PlaylistBrief,
  PlaylistOrderBy,
} from '../../utils/redux/playlistSlice'
import { OrderDirection } from '../../utils/dto'
import { apiBaseURL } from '../../utils/api'

import '../../components/PlaylistSection.scss'
import './PlayListCollection.scss'
import SearchInput from '../../components/SearchInput/SearchInput'

type CategoryType = 'mood' | 'style' | 'daytime' | 'business'

type FolderItem = {
  _id: string
  title: string
  image: string
}

const PHOTO =
  'https://img.freepik.com/free-photo/still-life-wireless-cyberpunk-headphones_23-2151072227.jpg?semt=ais_user_personalization&w=740&q=80'

const foldersByType: Record<CategoryType, FolderItem[]> = {
  style: [
    { _id: 'kpop', title: 'Kpop', image: PHOTO },
    { _id: 'rnb', title: 'R&B', image: PHOTO },
    { _id: 'pop', title: 'Pop', image: PHOTO },
    { _id: 'indie', title: 'Indie', image: PHOTO },
    { _id: 'folk', title: 'Folk', image: PHOTO },
    { _id: 'jazz', title: 'Jazz', image: PHOTO },
    { _id: 'rock', title: 'Rock', image: PHOTO },
    { _id: 'edm', title: 'EDM', image: PHOTO },
    { _id: 'rap', title: 'Rap', image: PHOTO },
    { _id: 'lofi', title: 'Lo-Fi', image: PHOTO },
    { _id: 'house', title: 'House', image: PHOTO },
    { _id: 'latin', title: 'Latin', image: PHOTO },
  ],
  mood: [],
  daytime: [],
  business: [],
}

const folderToParam: Record<CategoryType, string> = {
  style: 'style',
  business: 'business',
  mood: 'mood',
  daytime: 'daytime',
}

function isCategoryType(v: any): v is CategoryType {
  return v === 'mood' || v === 'style' || v === 'daytime' || v === 'business'
}

function prettifyType(v: CategoryType) {
  const map: Record<CategoryType, string> = {
    mood: 'Mood',
    style: 'Style',
    daytime: 'Daytime',
    business: 'Business',
  }
  return map[v] ?? String(v)
}

const PLAYLIST_IMG_BASE = `${String(apiBaseURL).replace(/\/$/, '')}/uploaded/playlists`

export default function PlayListCollection() {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { t, i18n } = useTranslation('home')

  const params = useParams<{ type?: string; id?: string }>()

  const rawType = params.type ?? params.id
  const type: CategoryType | null = isCategoryType(rawType) ? rawType : null

  const folderId = params.type ? params.id : undefined
  const isPlaylistsView = Boolean(type && folderId)

  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)')
    const onChange = () => setIsMobile(mq.matches)
    onChange()
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  const [search, setSearch] = useState('')

  const [folderMeta, setFolderMeta] = useState<{ title?: string; image?: string } | null>(
    null,
  )

  useEffect(() => {
    if (!type || !folderId) return

    const st: any = location.state
    const key = `plc:${type}:${folderId}`

    if (st?.title || st?.image) {
      setFolderMeta({ title: st.title, image: st.image })
      try {
        localStorage.setItem(key, JSON.stringify({ title: st.title, image: st.image }))
      } catch {}
      return
    }

    try {
      const raw = localStorage.getItem(key)
      if (raw) setFolderMeta(JSON.parse(raw))
    } catch {}
  }, [type, folderId, location.state])

  const [playlists, setPlaylists] = useState<PlaylistBrief[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const fetchPlaylists = async () => {
      if (!type || !folderId) return
      try {
        setLoading(true)

        const result = await dispatch(
          attemptGetPlaylistsFolderList({
            skip: 0,
            limit: 200,
            oderBy: PlaylistOrderBy.TITLE,
            oderDirection: OrderDirection.DESC,
            [folderToParam[type]]: folderId,
          }),
        ).unwrap()

        setPlaylists(result.playlists ?? [])
      } catch (e) {
        console.error('Error fetching category playlists:', e)
        setPlaylists([])
      } finally {
        setLoading(false)
      }
    }

    fetchPlaylists()
  }, [type, folderId, dispatch])

  const folders: FolderItem[] = useMemo(() => {
    if (!type) return []
    return foldersByType[type] ?? []
  }, [type])

  const cover = folderMeta?.image || PHOTO

  const headerTitle = useMemo(() => {
    if (!type) return 'Collection'

    if (isPlaylistsView) {
      return (
        (folderMeta?.title && String(folderMeta.title).trim()) ||
        t(type, { defaultValue: prettifyType(type) })
      )
    }

    return t(type, { defaultValue: prettifyType(type) })
  }, [type, isPlaylistsView, folderMeta?.title, t, i18n.language])

  const headerOverline = useMemo(() => {
    if (!type) return 'CHOOSE'
    const up = String(t(type, { defaultValue: prettifyType(type) })).toUpperCase()
    return isPlaylistsView ? `PLAYLISTS • ${up}` : `CHOOSE YOUR ${up}`
  }, [type, isPlaylistsView, t, i18n.language])

  const filteredFolders = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return folders
    return folders.filter((it) => it.title.toLowerCase().includes(q))
  }, [folders, search, i18n.language])

  const filteredPlaylists = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return playlists
    return playlists.filter((p) =>
      String(p.title ?? '')
        .toLowerCase()
        .includes(q),
    )
  }, [playlists, search, i18n.language])

  const SearchInputAny = SearchInput as any

  const openFolder = (folder: FolderItem) => {
    if (!type) return
    const key = `plc:${type}:${folder._id}`

    try {
      localStorage.setItem(
        key,
        JSON.stringify({ title: folder.title, image: folder.image }),
      )
    } catch {}

    navigate(`/playlist-collection/${type}/${folder._id}`, {
      state: { title: folder.title, image: folder.image },
    })
  }

  if (!type) {
    return (
      <div className="st st--invalid">
        <div className="st__invalid">
          Неверный тип. Ожидаю: mood / style / daytime / business
        </div>
      </div>
    )
  }

  return (
    <div
      className="st"
      style={
        {
          ['--st-bg' as any]: `url(${cover})`,
          ['--st-avatar' as any]: `url(${cover})`,
        } as CSSProperties
      }
    >
      {!isMobile && (
        <div className="st-top">
          <SearchInputAny
            value={search}
            onChange={(e: any) => setSearch(e?.target?.value ?? '')}
          />
        </div>
      )}

      <div className="st-wrap" style={{ marginTop: isMobile ? 0 : 100 }}>
        {!isMobile && (
          <div className="st-head">
            <button
              type="button"
              className="st-back"
              onClick={() => navigate(-1)}
              aria-label="Back"
            >
              <LeftOutlined />
            </button>

            <div className="st-headText">
              <div className="st-titleRow">
                <div className="st-overline">{headerOverline}</div>
                <div className="st-title">{headerTitle}</div>
              </div>
            </div>
          </div>
        )}

        {isMobile && (
          <div className="st-hero">
            <div className="st-heroTop">
              <button
                type="button"
                className="st-heroBtn"
                onClick={() => navigate(-1)}
                aria-label="Back"
              >
                <LeftOutlined />
              </button>

              <button
                style={{ visibility: 'hidden' }}
                type="button"
                className="st-heroBtn"
                aria-label="Search"
                onClick={() => {
                  const v = prompt('Search') ?? ''
                  setSearch(v)
                }}
              >
                <SearchOutlined />
              </button>
            </div>

            {/* <div className="st-heroArt" aria-hidden="true" /> */}

            <div className="st-heroText">
              <div className="st-heroTitle">{headerTitle}</div>
              <div className="st-heroSub">{headerOverline}</div>
            </div>
          </div>
        )}

        <section className="ps ps--tiles">
          {isPlaylistsView ? (
            loading ? (
              <div className="category-loading">
                <Spin size="large" />
              </div>
            ) : (
              <div className="st-grid" aria-label="Playlists grid">
                {filteredPlaylists.map((pl) => {
                  const idKey = String(pl._id ?? pl.title ?? '')
                  const grad = getGradientById(idKey, type)

                  const imgSrc = pl.image ? `${PLAYLIST_IMG_BASE}/${pl.image}` : PHOTO
                  const songsCountText =
                    pl.songsCount != null && Number(pl.songsCount) > 0
                      ? `${pl.songsCount} ${t('songs', { ns: 'playlist', defaultValue: 'Songs' })}`
                      : ''

                  return (
                    <button
                      key={idKey}
                      type="button"
                      className="ps-tile"
                      onClick={() => navigate(`/playlist/${pl._id}`)}
                      title={pl.title}
                      style={
                        {
                          ['--tile-a' as any]: grad.a,
                          ['--tile-b' as any]: grad.b,
                        } as CSSProperties
                      }
                    >
                      <div className="ps-tile__label">
                        <div className="ps-tile__title">{pl.title}</div>
                        {songsCountText ? (
                          <div className="ps-tile__meta">{songsCountText}</div>
                        ) : null}
                      </div>

                      <div className="ps-tile__artWrap">
                        <img
                          className="ps-tile__art"
                          src={imgSrc}
                          alt={pl.title}
                          loading="lazy"
                        />
                      </div>
                    </button>
                  )
                })}
              </div>
            )
          ) : (
            <div className="st-grid" aria-label="Folders grid">
              {filteredFolders.map((item) => {
                const idKey = String(item._id ?? item.title)
                const grad = getGradientById(idKey, type)

                return (
                  <button
                    key={idKey}
                    type="button"
                    className="ps-tile"
                    onClick={() => openFolder(item)}
                    title={item.title}
                    style={
                      {
                        ['--tile-a' as any]: grad.a,
                        ['--tile-b' as any]: grad.b,
                      } as CSSProperties
                    }
                  >
                    <div className="ps-tile__label">
                      <div className="ps-tile__title">{item.title}</div>
                    </div>
                    <div className="ps-tile__artWrap">
                      <img
                        className="ps-tile__art"
                        src={item.image}
                        alt={item.title}
                        loading="lazy"
                      />
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
